import {
    createProperties,
    createRoot,
    type Field,
    Form,
    type FormState,
    type TypeToFields,
} from '@jmvdevelop/svelte-form';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import { fail, ok, type Result } from '$lib/utils/result.js';
import { temporizePromise } from '$lib/utils/temporizePromise.js';
import { documentName } from '$gql/documentName';
import { useFragment } from '$gql/fragment-masking.js';
import {
    CreateFeedCommentMutationDocument,
    type FeedCommentFragment,
    FeedCommentFragmentDoc,
    FeedCommentQueryDocument,
    FeedPostQueryDocument,
    type ParticipantInput,
    FeedPostCommentsQueryDocument,
} from '$gql/graphql.js';

export type FeedCommentFormData = {
    author: ParticipantInput;
    body: string;
};

export type Fields<TFormValues> = TypeToFields<TFormValues, FeedCommentFormData>;

export function createFields<TValues>(
    self: Field<TValues, FeedCommentFormData>,
): Fields<TValues> {
    return {
        self: self,
        ...createProperties(self, ['body', 'author'] as const),
    };
}

export function validate(
    _form: Form<FeedCommentFormData, string>,
    _fields: Fields<FeedCommentFormData>,
    state: FormState<FeedCommentFormData, string>,
) {
    //const values = state.values;
    /*state = notBlankAndNotNull(values.body, {
        form,
        field: fields.body,
        message: 'Vous devez saisir le contenu du commentaire',
    })(state);*/
    return state;
}

export function createForm(
    id: string,
    initialValues: FeedCommentFormData,
): [Form<FeedCommentFormData, string>, Fields<FeedCommentFormData>] {
    const fields = createFields<FeedCommentFormData>(createRoot<FeedCommentFormData>());
    const form = new Form<FeedCommentFormData, string>({ initialValues }, id);

    form.addValidator((state) => {
        return validate(form, fields, state);
    });

    return [form, fields];
}

export type OnSubmit = (
    form: Form<FeedCommentFormData, string>,
) => Promise<Result<FeedCommentFragment, string>>;

export const createOnSubmit: (
    feedPost: number,
    parent: number | null,
    container: Pick<ShareableContainer, 'apollo' | 'datalayer'>,
) => OnSubmit =
    (feedPost, parent, { apollo, datalayer }) =>
    (form) => {
        return temporizePromise(
            500,
            (async () => {
                try {
                    const result = await apollo().mutate({
                        mutation: CreateFeedCommentMutationDocument,
                        variables: {
                            ...form.getState().values,

                            feedPost,
                            parent,
                        },
                        refetchQueries: [
                            documentName(FeedPostQueryDocument),
                            documentName(FeedCommentQueryDocument),
                            documentName(FeedPostCommentsQueryDocument),
                        ],
                    });
                    if (result.errors != null || result.data == null) {
                        return fail(result.errors?.toString() ?? 'Unknown error');
                    }
                    let comment = useFragment(
                        FeedCommentFragmentDoc,
                        result.data.feed.comment,
                    );

                    datalayer()?.push({
                        event: 'feed_comment.create',
                        feed_comment: {
                            id: comment.id,
                            post_id: feedPost,
                            comment_parent_id: parent,
                        },
                    });

                    return ok(comment);
                } catch {
                    return fail('Erreur inconnue.');
                }
            })(),
        );
    };
